import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, GripVertical, Trash2, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { PlaylistModule, PlaylistLesson } from '@/store/playlist/types';
import { VideoSelectModal } from '@/components/playlist/VideoSelectModal';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { LessonDescriptionEditor } from '@/components/playlist/editor/LessonDescriptionEditor';
import { validateUrl } from '@/lib/security';
import { cn } from '@/lib/utils';

interface ModuleVideoSectionProps {
  module: PlaylistModule;
  moduleIndex: number;
  onUpdateModule: (moduleId: string, updates: Partial<PlaylistModule>) => void;
  availableVideos: any[];
}

const mockAvailableVideos = [
  {
    id: 'video-1',
    name: 'Introdução ao Marketing Digital',
    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400',
    duration: 320,
    views: 1250,
  },
  {
    id: 'video-2',
    name: 'SEO Básico para Iniciantes',
    thumbnail: 'https://images.unsplash.com/photo-1432888622747-4eb9a8f2c293?w=400',
    duration: 480,
    views: 890,
  },
  {
    id: 'video-3',
    name: 'Google Ads - Primeiros Passos',
    thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400',
    duration: 420,
    views: 1100,
  },
];

export const ModuleVideoSection: React.FC<ModuleVideoSectionProps> = ({
  module,
  moduleIndex,
  onUpdateModule,
  availableVideos = mockAvailableVideos,
}) => {
  useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedLessons, setExpandedLessons] = useState<Set<string>>(new Set());
  const [urlErrors, setUrlErrors] = useState<Record<string, string>>({});

  const validateLessonUrl = (lessonId: string, url: string) => {
    if (!url || url.trim() === '') {
      setUrlErrors(prev => {
        const next = { ...prev };
        delete next[lessonId];
        return next;
      });
      return;
    }

    const validation = validateUrl(url);
    
    if (!validation.isValid) {
      setUrlErrors(prev => ({ ...prev, [lessonId]: translateInterfaceText("Insira uma URL válida") }));
    } else {
      setUrlErrors(prev => {
        const next = { ...prev };
        delete next[lessonId];
        return next;
      });
    }
  };

  const toggleLesson = (lessonId: string) => {
    setExpandedLessons(prev => {
      const next = new Set(prev);
      if (next.has(lessonId)) {
        next.delete(lessonId);
      } else {
        next.add(lessonId);
      }
      return next;
    });
  };

  const handleAddVideos = (selectedVideoIds: string[]) => {
    const newLessons: PlaylistLesson[] = selectedVideoIds
      .filter(id => !module.lessons.some(l => l.videoId === id))
      .map((id, index) => {
        const videoData = availableVideos.find(v => v.id === id);
        return {
          id: `lesson-${Date.now()}-${index}`,
          moduleId: module.id,
          order: module.lessons.length + index,
          title: videoData?.name || `Aula ${module.lessons.length + index + 1}`,
          duration: videoData?.duration || 0,
          videoId: id,
          thumbnailUrl: videoData?.thumbnail,
          description: '',
          ctaButton: {
            enabled: false,
            text: '',
            url: '',
          },
        };
      });

    onUpdateModule(module.id, {
      lessons: [...module.lessons, ...newLessons],
    });
    setIsModalOpen(false);
  };

  const handleRemoveLesson = (lessonId: string) => {
    const updatedLessons = module.lessons
      .filter(l => l.id !== lessonId)
      .map((lesson, index) => ({ ...lesson, order: index }));
    
    onUpdateModule(module.id, { lessons: updatedLessons });
  };

  const handleUpdateLesson = (lessonId: string, updates: Partial<PlaylistLesson>) => {
    const updatedLessons = module.lessons.map(l =>
      l.id === lessonId ? { ...l, ...updates } : l
    );
    onUpdateModule(module.id, { lessons: updatedLessons });
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      {/* Header do Módulo */}
      <div className="flex items-center gap-3">
        {module.thumbnailUrl && (
          <img
            src={module.thumbnailUrl}
            alt={module.title}
            className="w-16 h-28 object-cover rounded-lg"
          />
        )}
        <div className="flex-1">
          <h3 className="font-semibold text-foreground">
            {translateInterfaceText("Módulo")}{" "}{moduleIndex + 1}: {module.title}
          </h3>
          <p className="text-sm text-muted-foreground">
            {module.lessons.length} {module.lessons.length === 1 ? translateInterfaceText("aula") : translateInterfaceText("aulas")}
          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          {translateInterfaceText("Adicionar Aula")}</Button>
      </div>

      {/* Lista de Aulas */}
      {module.lessons.length > 0 ? (
        <div className="space-y-2">
          {module.lessons.map((lesson, index) => {
            const videoData = availableVideos.find(v => v.id === lesson.videoId);
            const isExpanded = expandedLessons.has(lesson.id);

            return (
              <div
                key={lesson.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Cabeçalho da Aula */}
                <div className="flex items-center gap-3 p-3 bg-gray-50">
                  <button className="cursor-grab text-gray-400 hover:text-gray-600">
                    <GripVertical className="w-4 h-4" />
                  </button>
                  
                  {videoData?.thumbnail && (
                    <img
                      src={videoData.thumbnail}
                      alt={lesson.title}
                      className="w-16 h-9 object-cover rounded"
                    />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{lesson.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDuration(lesson.duration)}
                    </p>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleLesson(lesson.id)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveLesson(lesson.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Conteúdo Expandido */}
                {isExpanded && (
                  <div className="p-4 space-y-4 bg-white border-t">
                    {/* Descrição */}
                    <div className="space-y-2">
                      <Label>{translateInterfaceText("Descrição da Aula")}</Label>
                      <LessonDescriptionEditor
                        value={lesson.description || ''}
                        onChange={(value) => handleUpdateLesson(lesson.id, { description: value })}
                        placeholder={translateInterfaceText("Digite a descrição da aula...")}
                      />
                    </div>

                    {/* CTA Button */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>{translateInterfaceText("Botão CTA")}</Label>
                        <Switch
                          checked={lesson.ctaButton?.enabled || false}
                          onCheckedChange={(checked) =>
                            handleUpdateLesson(lesson.id, {
                              ctaButton: {
                                ...lesson.ctaButton,
                                enabled: checked,
                                text: lesson.ctaButton?.text || '',
                                url: lesson.ctaButton?.url || '',
                              },
                            })
                          }
                        />
                      </div>

                      {lesson.ctaButton?.enabled && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label className="text-xs">{translateInterfaceText("Texto do Botão")}</Label>
                            <Input
                              value={lesson.ctaButton.text}
                              onChange={(e) =>
                                handleUpdateLesson(lesson.id, {
                                  ctaButton: {
                                    ...lesson.ctaButton!,
                                    text: e.target.value,
                                  },
                                })
                              }
                              placeholder={translateInterfaceText("Saiba Mais")}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">{translateInterfaceText("URL")}</Label>
                            <Input
                              value={lesson.ctaButton.url}
                              onChange={(e) => {
                                const url = e.target.value;
                                validateLessonUrl(lesson.id, url);
                                handleUpdateLesson(lesson.id, {
                                  ctaButton: {
                                    ...lesson.ctaButton!,
                                    url: url,
                                  },
                                });
                              }}
                              onBlur={(e) => validateLessonUrl(lesson.id, e.target.value)}
                              placeholder="https://..."
                              className={cn(
                                urlErrors[lesson.id] && "border-red-500 focus-visible:ring-red-500"
                              )}
                            />
                            {urlErrors[lesson.id] && (
                              <p className="text-xs text-red-500 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {urlErrors[lesson.id]}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">{translateInterfaceText("Nenhuma aula adicionada ainda")}</p>
        </div>
      )}

      {/* Modal de Seleção */}
      <VideoSelectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleAddVideos}
        availableVideos={availableVideos}
        selectedVideoIds={module.lessons.map(l => l.videoId)}
      />
    </div>
  );
};
